import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import '../../components/ConnectionsPage.css';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import { API_ENDPOINTS, BACKEND_NODE_URL } from '../../config/constants';
import AppIcon from '../../components/icons/AppIcon';
 

const ConnectionsPage = () => {
  const { user, reloadAuth } = useAuth();
  const navigate = useNavigate();
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('whatsapp');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingConnectionId, setEditingConnectionId] = useState(null);
  
  // Dados fictícios para conexões WhatsApp
  const [whatsappConnections, setWhatsappConnections] = useState([]);
  const [telegramConnections, setTelegramConnections] = useState([]);
  const [planInfo, setPlanInfo] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [planError, setPlanError] = useState('');
  const [companyId, setCompanyId] = useState(null);

  // Mapeia status do banco para os rótulos usados na UI
  const mapStatusToUi = (status) => {
    const s = (status || '').toString().toUpperCase();
    if (s === 'CONECTADO') return 'conectado';
    if (s === 'DESCONECTADO') return 'desconectado';
    if (s === 'PENDENTE') return 'pendente';
    if (s === 'ERRO') return 'erro';
    return 'desconhecido';
  };

  // Carregar conexões do Supabase por company_id, com fallback local
  const loadConnections = async () => {
    const effectiveCompanyId = companyId || user?.company_id || null;
    if (!effectiveCompanyId) {
      // fallback imediato ao localStorage se usuário/empresa não estiver disponível
      try {
        const raw = localStorage.getItem('connections');
        const arr = raw ? JSON.parse(raw) : [];
        const cached = Array.isArray(arr) ? arr : [];
        setWhatsappConnections(cached.filter((r) => r.type === 'whatsapp'));
        setTelegramConnections(cached.filter((r) => r.type === 'telegram'));
      } catch (_) {
        setWhatsappConnections([]);
        setTelegramConnections([]);
      }
      return;
    }
    try {
      const { data, error } = await supabase
        .from('connections')
        .select('id, name, description, type, number, status, provider_id, with_ai, ai_id, telegram_bot_username, created_at, updated_at')
        .eq('company_id', effectiveCompanyId)
        .order('updated_at', { ascending: false });
      if (error) throw error;

      let statusById = {};
      try {
        const params = new URLSearchParams({ company_id: effectiveCompanyId });
        const statusResp = await fetch(`${API_ENDPOINTS.CONNECTIONS_STATUS}?${params.toString()}`);
        if (statusResp.ok) {
          const payload = await statusResp.json();
          const list = Array.isArray(payload?.connections) ? payload.connections : [];
          statusById = list.reduce((acc, item) => {
            if (item?.id) acc[item.id] = item.status || null;
            return acc;
          }, {});
        } else {
          console.warn('[ConnectionsPage] Falha ao consultar status Uazapi:', statusResp.status);
        }
      } catch (statusErr) {
        console.warn('[ConnectionsPage] Status Uazapi indisponível:', statusErr?.message);
      }

      const rows = Array.isArray(data) ? data : [];
      const normalized = rows.map((r) => {
          const mergedStatus = statusById[r.id] || r.status;
          return {
            id: r.id,
            number: r.number || '-',
            name: r.name || 'Sem nome',
            status: mapStatusToUi(mergedStatus),
            description: r.description || '',
            type: r.type,
            provider_id: r.provider_id || null,
            with_ai: !!r.with_ai,
            ai_id: r.ai_id || null,
            telegram_bot_username: r.telegram_bot_username || null,
            created_at: r.created_at,
            updated_at: r.updated_at,
          };
        });
      const onlyWhatsapp = normalized.filter((r) => r.type === 'whatsapp');
      const onlyTelegram = normalized.filter((r) => r.type === 'telegram');
      setWhatsappConnections(onlyWhatsapp);
      setTelegramConnections(onlyTelegram);
      // opcional: cache local para fallback
      try { localStorage.setItem('connections', JSON.stringify(normalized)); } catch (_) {}
    } catch (err) {
      console.error('Falha ao carregar conexões do Supabase, usando cache local:', err);
      try {
        const raw = localStorage.getItem('connections');
        const arr = raw ? JSON.parse(raw) : [];
        const cached = Array.isArray(arr) ? arr : [];
        setWhatsappConnections(cached.filter((r) => r.type === 'whatsapp'));
        setTelegramConnections(cached.filter((r) => r.type === 'telegram'));
      } catch (_) {
        setWhatsappConnections([]);
        setTelegramConnections([]);
      }
    } finally {
      setInitialLoading(false);
    }
  };

  const loadPlanInfo = useCallback(async () => {
    try {
      setPlanLoading(true);
      setPlanError('');

      let currentUser = user;
      if (!currentUser?.id && reloadAuth) {
        try {
          await reloadAuth();
          currentUser = user;
        } catch (err) {
          console.warn('[ConnectionsPage] Falha ao recarregar auth:', err);
        }
      }

      if (!currentUser?.id) {
        setPlanInfo(null);
        setPlanError('Usuário não autenticado.');
        return;
      }

      const params = new URLSearchParams({ userId: currentUser.id });
      if (companyId || currentUser.company_id) {
        params.append('companyId', companyId || currentUser.company_id);
      }

      const response = await fetch(`${BACKEND_NODE_URL}/api/company/plan?${params.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Falha ao carregar dados do plano.');
      }

      const { subscription } = await response.json();
      setPlanInfo(subscription || null);
    } catch (error) {
      console.error('[ConnectionsPage] Erro ao carregar plano:', error);
      setPlanInfo(null);
      setPlanError(error?.message || 'Erro ao carregar plano.');
    } finally {
      setPlanLoading(false);
    }
  }, [reloadAuth, user]);

  useEffect(() => {
    loadConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.company_id, companyId]);

  useEffect(() => {
    (async () => {
      try {
        if (!user?.id) return;
        const { data, error } = await supabase
          .from('profiles')
          .select('company_id')
          .eq('id', user.id)
          .maybeSingle();
        if (!error && data?.company_id) setCompanyId(data.company_id);
      } catch (_) {}
    })();
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      loadPlanInfo();
    }
  }, [user?.id, user?.company_id, loadPlanInfo]);

  
  // Estado para controlar a visibilidade do modal de QRCode
  const [qrCodeModal, setQrCodeModal] = useState({
    isOpen: false,
    connectionId: null,
    connectionName: '',
    qrcodeData: '',
    profileName: '',
    profilePicUrl: '',
    showPhoneInput: false // ✅ Controla se mostra input de telefone
  });
  // Indicador de carregamento enquanto solicita o QRCode ao backend
  const [qrLoading, setQrLoading] = useState(false);
  // Erro ao carregar/validar a imagem do QRCode
  const [qrError, setQrError] = useState('');
  // Controle de polling e concorrência
  const qrPollTimerRef = useRef(null);
  const qrIsFetchingRef = useRef(false);
  // Estado para input de telefone
  const [phoneInput, setPhoneInput] = useState('');
  // Polling para detectar conexão estabelecida
  const connectionCheckTimerRef = useRef(null);
  // Preview de imagem em tela cheia
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const openImagePreview = (url) => {
    if (!url) return;
    setPreviewImageUrl(url);
  };
  const closeImagePreview = () => setPreviewImageUrl('');
  
  // Dados fictícios para agentes de IA
  const [aiAgents, setAiAgents] = useState([]);
  const [aiAgentsLoading, setAiAgentsLoading] = useState(false);
  const [aiAgentsError, setAiAgentsError] = useState('');

  const fetchAiAgents = async () => {
    if (!user?.company_id) {
      setAiAgentsError('Empresa não definida para o usuário.');
      setAiAgents([]);
      return;
    }
    try {
      setAiAgentsError('');
      setAiAgentsLoading(true);
      
      // 1. Buscar IDs de agentes que já estão vinculados a conexões (EXCETO a conexão atual sendo editada)
      const { data: connectedAgents, error: connError } = await supabase
        .from('connections')
        .select('agente_id, id')
        .eq('company_id', user.company_id)
        .not('agente_id', 'is', null);
      
      if (connError) throw connError;
      
      // Filtrar agentes vinculados a OUTRAS conexões (não a atual)
      const connectedAgentIds = (connectedAgents || [])
        .filter(c => c.id !== editingConnectionId) // Excluir a conexão atual
        .map(c => c.agente_id)
        .filter(Boolean);
      
      // 2. Buscar agentes WhatsApp (canal='WhatsApp') que NÃO estão vinculados
      const { data: agentes, error } = await supabase
        .from('agentes')
        .select('id, nome, descricao, empresa_id, canal, modelo')
        .eq('empresa_id', user.company_id)
        .eq('canal', 'WhatsApp') // Apenas WhatsApp
        .neq('canal', 'Interno'); // Ignore internos
      
      if (error) throw error;
      
      const rows = Array.isArray(agentes) ? agentes : [];
      
      // 3. Filtrar apenas agentes que NÃO estão conectados a OUTRAS conexões
      const availableAgents = rows.filter(r => !connectedAgentIds.includes(r.id.toString()));
      
      // 4. Normalizar para exibição
      const normalized = availableAgents.map(r => ({
        id: r.id,
        name: r.nome || 'Sem nome',
        description: r.descricao || '',
        modelo: r.modelo || 'nano',
        canal: r.canal || 'WhatsApp'
      }));
      
      setAiAgents(normalized);
    } catch (e) {
      console.error('Falha ao carregar agentes de IA:', e);
      setAiAgentsError(e?.message || 'Falha ao carregar agentes');
      setAiAgents([]);
    } finally {
      setAiAgentsLoading(false);
    }
  };

  // Estado para os dados do formulário do modal
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'atendimento',
    syncContacts: false,
    connectWithAI: false,
    selectedAgent: '',
    telegramBotToken: '',
    telegramBotUsername: ''
  });

  // Estado do formulário de edição (mesma estrutura do de criação)
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    type: 'atendimento',
    syncContacts: false,
    connectWithAI: false,
    selectedAgent: '',
    telegramBotToken: '',
    telegramBotUsername: ''
  });
  // Detalhes do perfil para o modal de edição
  const [editProfileName, setEditProfileName] = useState('');
  const [editProfilePicUrl, setEditProfilePicUrl] = useState('');
  const [editProfileNameInput, setEditProfileNameInput] = useState('');
  const [editProfileSaving, setEditProfileSaving] = useState(false);
  const [editProfileImageSaving, setEditProfileImageSaving] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  
  // Carrega agentes quando abrir o modal de criação e a opção "Conectar com IA" estiver ativa
  useEffect(() => {
    if (isModalOpen && formData.connectWithAI) {
      fetchAiAgents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, formData.connectWithAI, user?.company_id]);

  // Carrega agentes quando abrir o modal de edição e a opção "Conectar com IA" estiver ativa
  useEffect(() => {
    if (isEditModalOpen && editFormData.connectWithAI) {
      fetchAiAgents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditModalOpen, editFormData.connectWithAI, user?.company_id]);

  // Ao abrir o modal de edição, buscar detalhes da conexão (foto/nome do perfil)
  useEffect(() => {
    const loadEditDetails = async () => {
      if (!isEditModalOpen || !editingConnectionId) return;
      setEditProfilePicUrl('');
      try {
        const resp = await fetch(`${API_ENDPOINTS.CONNECTIONS_DETAILS_BASE}/${editingConnectionId}/details`, {
          headers: { 'Accept': 'application/json' }
        });
        if (!resp.ok) return;
        const { details } = await resp.json();
        setEditProfileName(details?.profile_name || '');
        if (details?.profile_pic_url) {
          const cacheBuster = `t=${Date.now()}`;
          const sep = details.profile_pic_url.includes('?') ? '&' : '?';
          setEditProfilePicUrl(`${details.profile_pic_url}${sep}${cacheBuster}`);
        } else {
          setEditProfilePicUrl('');
        }
        setEditProfileNameInput(details?.profile_name || '');
      } catch (_) {
        // silencioso
      }
    };
    loadEditDetails();
    // limpar ao fechar
    if (!isEditModalOpen) {
      setEditProfileName('');
      setEditProfilePicUrl('');
      setEditProfileNameInput('');
      setEditProfileSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditModalOpen, editingConnectionId]);
  
  const planLimits = planInfo?.plans || null;
  const maxConnections = planLimits?.max_whatsapp_connections ?? null;
  const hasUnlimitedConnections = !planLimits || maxConnections === null || maxConnections >= 999999;
  const connectionsCount = whatsappConnections.length;
  const reachedLimit = !hasUnlimitedConnections && typeof maxConnections === 'number' && maxConnections >= 0 && connectionsCount >= maxConnections;

  const handleNewConnection = () => {
    if (reachedLimit) {
      alert('Você atingiu o limite de conexões do seu plano. Faça upgrade para adicionar novas conexões.');
      return;
    }
    setIsModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Resetar o formulário
    setFormData({
      name: '',
      description: '',
      type: 'atendimento',
      syncContacts: false,
      connectWithAI: false,
      selectedAgent: '',
      telegramBotToken: '',
      telegramBotUsername: ''
    });
  };
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleSaveConnection = async () => {
    if (reachedLimit) {
      alert('Você atingiu o limite de conexões do seu plano. Faça upgrade para adicionar novas conexões.');
      handleCloseModal();
      return;
    }
    if (activeTab === 'telegram' && !(formData.telegramBotToken || '').trim()) {
      alert('Informe o Token do bot do Telegram.');
      return;
    }
    if (!user?.id || !user?.company_id) {
      alert('Usuário/empresa não encontrados. Faça login novamente.');
      return;
    }
    try {
      // Envia somente os IDs conforme solicitado; backend cuidará do Supabase e integrações
      const resp = await fetch(API_ENDPOINTS.CONNECTIONS_CREATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json, */*;q=0.8',
        },
        body: JSON.stringify({
          user_id: user.id,
          company_id: user.company_id,
          // canal de conexão conforme aba ativa
          channel: activeTab === 'telegram' ? 'telegram' : 'whatsapp',
          // dados do formulário
          name: (formData.name || 'Nova Conexão').trim(),
          description: (formData.description || '').trim(),
          type: formData.type || 'atendimento',
          sync_contacts: activeTab === 'telegram' ? false : !!formData.syncContacts,
          connect_with_ai: !!formData.connectWithAI,
          ai_agent_id: formData.selectedAgent || null,
          ...(activeTab === 'telegram'
            ? {
                telegram_bot_token: (formData.telegramBotToken || '').trim(),
                ...(String(formData.telegramBotUsername || '').trim()
                  ? { telegram_bot_username: String(formData.telegramBotUsername || '').trim() }
                  : {})
              }
            : {})
        })
      });
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        throw new Error(`Falha ao criar conexão (${resp.status}) ${txt ? '- ' + txt.substring(0, 200) : ''}`);
      }
      // Pode retornar um objeto ou array; normalizamos
      let data;
      try {
        data = await resp.json();
      } catch (_) {
        data = null;
      }
      const row = Array.isArray(data) ? data[0] : (data || {});
      // Monta conexão para UI com fallback de campos
      const uiConn = {
        id: row.id ?? crypto.randomUUID?.() ?? String(Date.now()),
        number: row.number ?? '-',
        name: row.name ?? (formData.name || 'Nova Conexão'),
        status: mapStatusToUi(row.status ?? 'DESCONECTADO'),
        description: row.description ?? (formData.description || ''),
        type: row.type ?? (activeTab === 'telegram' ? 'telegram' : 'whatsapp'),
        provider_id: row.provider_id ?? null,
        with_ai: !!(row.with_ai ?? row.connect_with_ai ?? formData.connectWithAI),
        ai_id: row.ai_id ?? row.ai_agent_id ?? null,
        created_at: row.created_at ?? new Date().toISOString(),
        updated_at: row.updated_at ?? new Date().toISOString(),
      };
      if (activeTab === 'telegram') {
        setTelegramConnections(prev => {
          const updated = [uiConn, ...prev];
          try {
            const raw = localStorage.getItem('connections');
            const arr = raw ? JSON.parse(raw) : [];
            const cached = Array.isArray(arr) ? arr : [];
            const next = [uiConn, ...cached.filter((c) => c.id !== uiConn.id)];
            localStorage.setItem('connections', JSON.stringify(next));
          } catch (_) {}
          return updated;
        });
      } else {
        setWhatsappConnections(prev => {
          const updated = [uiConn, ...prev];
          try {
            const raw = localStorage.getItem('connections');
            const arr = raw ? JSON.parse(raw) : [];
            const cached = Array.isArray(arr) ? arr : [];
            const next = [uiConn, ...cached.filter((c) => c.id !== uiConn.id)];
            localStorage.setItem('connections', JSON.stringify(next));
          } catch (_) {}
          return updated;
        });
      }
      handleCloseModal();
    } catch (err) {
      console.error('Erro ao salvar conexão no Supabase:', err);
      alert(err.message || 'Falha ao salvar a nova conexão.');
    }
  };
  
  const handleEditConnection = (id) => {
    const list = activeTab === 'telegram' ? telegramConnections : whatsappConnections;
    const conn = list.find(c => c.id === id);
    if (!conn) return;
    setEditingConnectionId(id);
    setEditFormData({
      name: conn.name || '',
      description: conn.description || '',
      type: conn.type || 'atendimento',
      syncContacts: false,
      connectWithAI: !!conn.with_ai, // ✅ Pré-preencher se tem IA
      selectedAgent: conn.ai_id || '', // ✅ Pré-preencher agente vinculado
      telegramBotToken: '',
      telegramBotUsername: conn.telegram_bot_username || ''
    });
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingConnectionId(null);
    setEditFormData({
      name: '',
      description: '',
      type: 'atendimento',
      syncContacts: false,
      connectWithAI: false,
      selectedAgent: '',
      telegramBotToken: '',
      telegramBotUsername: ''
    });
  };

  const handleSaveEdit = async () => {
    try {
      setIsSavingEdit(true);
      if (!editingConnectionId) return;
      
      // Persistir no backend Node (Supabase via server)
      const resp = await fetch(`${API_ENDPOINTS.CONNECTIONS_DETAILS_BASE}/${editingConnectionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          name: (editFormData.name || '').trim(),
          description: (editFormData.description || '').trim(),
          type: editFormData.type || 'atendimento',
          connect_with_ai: !!editFormData.connectWithAI,
          ai_agent_id: editFormData.selectedAgent || null,
          ...(activeTab === 'telegram' && (editFormData.telegramBotToken || '').trim()
            ? { telegram_bot_token: (editFormData.telegramBotToken || '').trim() }
            : {})
        }),
      });
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        throw new Error(`Falha ao salvar conexão (${resp.status}) ${txt ? '- ' + txt.substring(0, 200) : ''}`);
      }
      const saved = await resp.json().catch(() => ({}));
      setWhatsappConnections(prev => {
        const updated = prev.map(conn => (
          conn.id === editingConnectionId
            ? { ...conn, ...saved, with_ai: editFormData.connectWithAI, ai_id: editFormData.selectedAgent }
            : conn
        ));
        try { localStorage.setItem('connections', JSON.stringify(updated)); } catch (_) {}
        return updated;
      });
      setTelegramConnections(prev => {
        const updated = prev.map(conn => (
          conn.id === editingConnectionId
            ? { ...conn, ...saved, with_ai: editFormData.connectWithAI, ai_id: editFormData.selectedAgent }
            : conn
        ));
        return updated;
      });

      // Se marcado para sincronizar contatos, chamar backend (múltiplos batches sequenciais)
      if (activeTab !== 'telegram' && editFormData.syncContacts) {
        const limit = 200; // tamanho do lote por chamada
        const maxIters = 50; // segurança para evitar loop infinito
        let totalImported = 0;
        let iter = 0;
        let lastErr = null;
        while (iter < maxIters) {
          iter++;
          try {
            const syncUrl = `${API_ENDPOINTS.CONNECTIONS_DETAILS_BASE}/${editingConnectionId}/sync-contacts?limit=${limit}`;
            const controller = new AbortController();
            const tm = setTimeout(() => controller.abort(), 130000);
            const syncResp = await fetch(syncUrl, { method: 'POST', headers: { 'Accept': 'application/json' }, signal: controller.signal });
            clearTimeout(tm);
            let syncData = null;
            try { syncData = await syncResp.json(); } catch {}
            if (!syncResp.ok || !syncData?.ok) {
              const msg = (syncData && (syncData.error || syncData.message)) || `Falha ao sincronizar contatos (${syncResp.status})`;
              throw new Error(msg);
            }
            const imported = Number(syncData.imported || 0);
            totalImported += imported;
            console.info(`[Connections] Sync batch ${iter}: imported=${imported}, total=${totalImported}`);
            if (imported < limit || imported === 0) break; // acabou ou não há mais o que importar
          } catch (e) {
            lastErr = e;
            console.warn(`[Connections] Sync contatos falhou no batch ${iter}:`, e?.message || e);
            break;
          }
        }
        if (lastErr) {
          alert(lastErr?.message || 'Falha ao sincronizar contatos');
        } else {
          alert(`Sincronização de contatos concluída. Importados/atualizados: ${totalImported}.`);
        }
      }

      handleCloseEditModal();
    } catch (e) {
      console.error('Erro ao salvar conexão (PUT):', e);
      alert(e?.message || 'Falha ao salvar conexão.');
    } finally {
      setIsSavingEdit(false);
    }
  };
  
  const handleDeleteConnection = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta conexão?')) return;
    if (!user?.id || !user?.company_id) {
      alert('Usuário/empresa não encontrados. Faça login novamente.');
      return;
    }
    try {
      const resp = await fetch(`${API_ENDPOINTS.CONNECTIONS_DETAILS_BASE}/${id}?hard=true`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json, */*;q=0.8',
        },
      });
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        throw new Error(`Falha ao excluir conexão (${resp.status}) ${txt ? '- ' + txt.substring(0, 200) : ''}`);
      }
      setWhatsappConnections(prev => {
        const updated = prev.filter(conn => conn.id !== id);
        try { localStorage.setItem('connections', JSON.stringify(updated)); } catch (_) {}
        return updated;
      });
      setTelegramConnections(prev => prev.filter(conn => conn.id !== id));
    } catch (err) {
      console.error('Erro ao excluir conexão via backend:', err);
      alert(err.message || 'Falha ao excluir a conexão.');
    }
  };

  // ✅ Função para alterar a imagem do perfil do WhatsApp (via upload de arquivo)
  const handleChangeProfileImage = async () => {
    if (!editingConnectionId) return;
    
    // Criar input file temporário
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/jpeg,image/jpg';
    fileInput.style.display = 'none';
    
    fileInput.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      
      // Validar tipo
      if (!file.type.includes('jpeg') && !file.type.includes('jpg')) {
        alert('Por favor, selecione uma imagem JPEG');
        return;
      }
      
      // Validar tamanho (máximo 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('A imagem não pode ter mais de 5MB');
        return;
      }
      
      try {
        setEditProfileImageSaving(true);
        
        // Converter arquivo para base64 (mesmo padrão que vocês usam em ProductsManager)
        const reader = new FileReader();
        reader.onloadend = async () => {
          const imageBase64 = reader.result;
          
          try {
            const resp = await fetch(API_ENDPOINTS.CONNECTIONS_PROFILE_IMAGE, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ connection_id: editingConnectionId, imageBase64 }),
            });
            
            if (!resp.ok) {
              const txt = await resp.text().catch(() => '');
              throw new Error(`Falha ao alterar imagem (${resp.status})${txt ? ' - ' + txt.substring(0, 200) : ''}`);
            }
            
            alert('Imagem do perfil alterada com sucesso!');
            
            // Recarregar detalhes para atualizar a imagem
            try {
              const detailsResp = await fetch(`${API_ENDPOINTS.CONNECTIONS_DETAILS_BASE}/${editingConnectionId}/details`);
              if (detailsResp.ok) {
                const { details } = await detailsResp.json();
                if (details?.profile_pic_url) {
                  const cacheBuster = `t=${Date.now()}`;
                  const sep = details.profile_pic_url.includes('?') ? '&' : '?';
                  setEditProfilePicUrl(`${details.profile_pic_url}${sep}${cacheBuster}`);
                }
                setEditProfileName(details?.profile_name || editProfileName);
              }
            } catch {}
          } catch (err) {
            const msg = err?.message || 'Falha ao alterar imagem do perfil';
            alert(msg);
          } finally {
            setEditProfileImageSaving(false);
          }
        };
        reader.onerror = () => {
          alert('Erro ao ler o arquivo');
          setEditProfileImageSaving(false);
        };
        reader.readAsDataURL(file);
      } catch (e) {
        const msg = e?.message || 'Falha ao processar imagem';
        alert(msg);
        setEditProfileImageSaving(false);
      }
    };
    
    // Disparar clique no input
    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  };

  const handleGenerateQRCode = async (id, opts = {}) => {
    const silent = !!opts.silent;
    const phone = opts.phone || null; // ✅ Aceita telefone como parâmetro
    
    // Encontrar a conexão pelo ID para obter o nome
    const connection = whatsappConnections.find(conn => conn.id === id);
    if (!connection) return;

    // Abre o modal imediatamente com placeholder enquanto busca o QRCode
    const transparentPng = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGMAAQAABQABDQottAAAAABJRU5ErkJggg==';
    setQrCodeModal({
      isOpen: true,
      connectionId: id,
      connectionName: connection.name,
      qrcodeData: transparentPng,
      profileName: '',
      profilePicUrl: '',
      showPhoneInput: false // ✅ Começa sem input de telefone
    });
    // Busca detalhes seguros (nome/foto do perfil) para enriquecer o modal
    try {
      const detailsResp = await fetch(`${API_ENDPOINTS.CONNECTIONS_DETAILS_BASE}/${id}/details`, { headers: { 'Accept': 'application/json' } });
      if (detailsResp.ok) {
        const { details } = await detailsResp.json();
        if (details) {
          setQrCodeModal(prev => ({
            ...prev,
            profileName: details.profile_name || '',
            profilePicUrl: details.profile_pic_url || ''
          }));
        }
      }
    } catch (e) {
      // silencioso
    }
    setQrLoading(true);
    setQrError('');

    // Evita requisições concorrentes
    if (qrIsFetchingRef.current) {
      return;
    }
    qrIsFetchingRef.current = true;

    if (!user?.id || !user?.company_id) {
      alert('Usuário/empresa não encontrados. Faça login novamente.');
      return;
    }

    // Helper: valida e garante carregamento de imagem por URL
    // Mantido para validar o blob gerado via URL.createObjectURL
    const ensureImage = (src) => new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => reject(new Error('Imagem inválida'));
      img.src = src;
    });

    try {
      // ✅ Não pede telefone aqui; será enviado apenas se o usuário clicar em "Parear com número"
      const resp = await fetch(API_ENDPOINTS.CONNECTIONS_QR, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'image/png, application/octet-stream;q=0.9, */*;q=0.8',
        },
        body: JSON.stringify({
          user_id: user.id,
          company_id: user.company_id,
          connection_id: id,
          channel: 'whatsapp',
          ...(phone ? { phone: String(phone).trim() } : {})
        })
      });
      console.debug('[QRCode] HTTP', resp.status, resp.statusText);
      let src = '';

      if (!resp.ok) {
        // Tenta extrair mensagem de erro do backend
        let errText = '';
        try { errText = await resp.text(); } catch {}
        throw new Error(`Falha ao gerar QRCode (${resp.status})${errText ? ' - ' + errText.substring(0, 200) : ''}`);
      }

      // Somente binário: ler blob e validar assinatura; se falhar, tentar JSON->URL/data
      const clone = resp.clone();
      const blob = await resp.blob();
      console.debug('[QRCode] blob size ~', blob.size, 'type:', blob.type || 'unknown');
      if (!blob || !blob.size) {
        throw new Error('Blob vazio retornado pelo backend');
      }
      // Validar assinatura PNG/JPEG
      const head = await blob.slice(0, 8).arrayBuffer();
      const bytes = new Uint8Array(head);
      const isPng = bytes.length >= 4 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47;
      const isJpg = bytes.length >= 2 && bytes[0] === 0xFF && bytes[1] === 0xD8;
      if (!isPng && !isJpg) {
        console.warn('[QRCode] Assinatura desconhecida no blob. Tentando exibir assim mesmo.');
      }
      src = URL.createObjectURL(blob);
      try {
        // Tenta validar a imagem resultante do blob primário
        await ensureImage(src);
        setQrCodeModal(prev => ({ ...prev, qrcodeData: src }));
        setQrError('');
      } catch (e) {
        console.warn('[QRCode] Validação falhou no blob principal, tentando JSON->URL:', e?.message);
        // Fallback binário via URL presente no JSON
        try {
          const json = await clone.json();
          const item = Array.isArray(json) ? json[0] : json;
          // Campos possíveis
          const inst = item?.instance;
          console.debug('[QRCode] JSON keys (top-level):', Object.keys(item || {}));
          if (inst && typeof inst === 'object') {
            try { console.debug('[QRCode] JSON keys (instance):', Object.keys(inst)); } catch {}
          }
          const urlCandidate = item?.qrcode_url || item?.image_url || item?.url || item?.qr_url;
          const paircode = inst?.paircode || item?.paircode;

          // Data URL direto (data:image/png;base64,...)
          let dataUrlCandidate = inst?.qrcode || item?.qrcode;

          if (dataUrlCandidate && /^data:image\//i.test(String(dataUrlCandidate))) {
            // Usa data URL diretamente
            await ensureImage(String(dataUrlCandidate));
            setQrCodeModal(prev => ({ ...prev, qrcodeData: String(dataUrlCandidate) }));
            setQrError('');
            return;
          }

          // Caso venha apenas base64 (sem prefixo data:)
          const base64Candidate = (
            (typeof inst?.qrcode === 'string' && inst?.qrcode) ||
            (typeof item?.qrcode === 'string' && item?.qrcode) ||
            inst?.qr || item?.qr ||
            inst?.qr_code || item?.qr_code ||
            inst?.qrcode_base64 || item?.qrcode_base64
          );
          if (base64Candidate && !/^data:image\//i.test(String(base64Candidate))) {
            const dataUrl = `data:image/png;base64,${String(base64Candidate)}`;
            try {
              await ensureImage(dataUrl);
              setQrCodeModal(prev => ({ ...prev, qrcodeData: dataUrl }));
              setQrError('');
              return;
            } catch (e2) {
              console.warn('[QRCode] base64->dataURL validation failed:', e2?.message);
            }
          }

          if (urlCandidate && /^https?:\/\//i.test(String(urlCandidate))) {
            const imgResp = await fetch(String(urlCandidate), { headers: { 'Accept': 'image/png,application/octet-stream;q=0.9,*/*;q=0.8' } });
            if (!imgResp.ok) throw new Error(`Falha ao baixar imagem (${imgResp.status})`);
            const imgBlob = await imgResp.blob();
            console.debug('[QRCode] URL blob size ~', imgBlob.size, 'type:', imgBlob.type || 'unknown');
            const urlSrc = URL.createObjectURL(imgBlob);
            await ensureImage(urlSrc);
            setQrCodeModal(prev => {
              if (prev.qrcodeData && prev.qrcodeData.startsWith('blob:')) URL.revokeObjectURL(prev.qrcodeData);
              return { ...prev, qrcodeData: urlSrc };
            });
            setQrError('');
            return;
          }

          if (paircode) {
            setQrError(`Código de pareamento: ${paircode}`);
            return;
          }

          throw new Error('JSON sem URL/data de imagem ou paircode');
        } catch (fallbackErr) {
          console.error('[QRCode] Fallback JSON->URL falhou:', fallbackErr?.message);
          setQrError('Falha ao carregar o QRCode. Tente novamente.');
        }
      }
    } catch (err) {
      console.error('Erro ao obter QRCode via backend:', err);
      if (!silent) {
        alert(err.message || 'Falha ao gerar QRCode.');
      }
    } finally {
      setQrLoading(false);
      qrIsFetchingRef.current = false;
    }
  };
  
  const handleCloseQRCodeModal = () => {
    // Revoga URL de objeto se houver (evita vazamento de memória)
    if (qrCodeModal.qrcodeData && qrCodeModal.qrcodeData.startsWith('blob:')) {
      URL.revokeObjectURL(qrCodeModal.qrcodeData);
    }
    // Limpa polling se ativo
    if (qrPollTimerRef.current) {
      clearInterval(qrPollTimerRef.current);
      qrPollTimerRef.current = null;
    }
    // Limpa polling de verificação de conexão
    if (connectionCheckTimerRef.current) {
      clearInterval(connectionCheckTimerRef.current);
      connectionCheckTimerRef.current = null;
    }
    setQrCodeModal({
      isOpen: false,
      connectionId: null,
      connectionName: '',
      qrcodeData: '',
      showPhoneInput: false
    });
    setPhoneInput('');
  };

  // Polling: enquanto o modal estiver aberto, atualizar o QR a cada 30s E verificar conexão a cada 5s
  useEffect(() => {
    if (qrCodeModal.isOpen && qrCodeModal.connectionId) {
      // evita múltiplos intervalos
      if (qrPollTimerRef.current) clearInterval(qrPollTimerRef.current);
      // agenda a próxima atualização em 30s (mas NÃO se estiver em modo de telefone)
      qrPollTimerRef.current = setInterval(() => {
        // ✅ NÃO regenera QRCode se estiver no modo "Parear com Número"
        if (!qrCodeModal.showPhoneInput) {
          // usa a mesma URL e fluxo existente em modo silencioso
          handleGenerateQRCode(qrCodeModal.connectionId, { silent: true });
        }
      }, 30000);
      
      // ✅ Polling para detectar quando a conexão foi estabelecida
      if (connectionCheckTimerRef.current) clearInterval(connectionCheckTimerRef.current);
      connectionCheckTimerRef.current = setInterval(async () => {
        try {
          const { data: connData } = await supabase
            .from('connections')
            .select('status')
            .eq('id', qrCodeModal.connectionId)
            .single();
          
          if (connData?.status === 'CONECTADO') {
            console.log('✅ Conexão estabelecida! Fechando modal e recarregando...');
            handleCloseQRCodeModal();
            // Recarregar conexões
            await loadConnections();
            // Recarregar página para atualizar tudo
            window.location.reload();
          }
        } catch (e) {
          console.warn('Erro ao verificar status da conexão:', e?.message);
        }
      }, 5000); // Verifica a cada 5 segundos
    } else {
      if (qrPollTimerRef.current) {
        clearInterval(qrPollTimerRef.current);
        qrPollTimerRef.current = null;
      }
      if (connectionCheckTimerRef.current) {
        clearInterval(connectionCheckTimerRef.current);
        connectionCheckTimerRef.current = null;
      }
    }
    return () => {
      if (qrPollTimerRef.current) {
        clearInterval(qrPollTimerRef.current);
        qrPollTimerRef.current = null;
      }
      if (connectionCheckTimerRef.current) {
        clearInterval(connectionCheckTimerRef.current);
        connectionCheckTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrCodeModal.isOpen, qrCodeModal.connectionId, qrCodeModal.showPhoneInput]);

  // Marca a conexão como estabelecida (status "conectado")
  const handleMarkConnected = async (id) => {
    if (!user?.id || !user?.company_id) {
      alert('Usuário/empresa não encontrados. Faça login novamente.');
      return;
    }
    try {
      const resp = await fetch(API_ENDPOINTS.CONNECTIONS_MARK_CONNECTED, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json, */*;q=0.8',
        },
        body: JSON.stringify({
          user_id: user.id,
          company_id: user.company_id,
          connection_id: id,
          channel: 'whatsapp',
        })
      });
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        throw new Error(`Falha ao sinalizar conexão estabelecida (${resp.status}) ${txt ? '- ' + txt.substring(0, 200) : ''}`);
      }
      // Sucesso: refletir no front
      setWhatsappConnections(prev => {
        const updated = prev.map(conn =>
          conn.id === id ? { ...conn, status: 'conectado' } : conn
        );
        try { localStorage.setItem('connections', JSON.stringify(updated)); } catch (_) {}
        return updated;
      });
      handleCloseQRCodeModal();
    } catch (err) {
      console.error('Erro ao sinalizar conexão estabelecida via backend:', err);
      alert(err.message || 'Falha ao sinalizar conexão estabelecida.');
    }
  };

  const getStatusIcon = (status) => {
    const s = status?.toLowerCase();
    switch (s) {
      case 'conectado':
        return '🟢';
      case 'desconectado':
      case 'pendente':
        // vermelho para desconectado e pendente
        return '🔴';
      case 'erro':
        return '⚠️';
      default:
        return '⚪';
    }
  };
  
  const getStatusText = (status) => {
    const s = status?.toLowerCase();
    switch (s) {
      case 'conectado':
        return 'Conectado';
      case 'desconectado':
        return 'Desconectado';
      case 'pendente':
        return 'Pendente';
      case 'erro':
        return 'Erro';
      default:
        return 'Desconhecido';
    }
  };
  
  if (initialLoading) {
    return (
      <div className="connections-page">
        <LoadingSpinner message="Carregando conexões..." />
      </div>
    );
  }

  return (
    <div className="connections-page">
      <div className="connections-tabs">
        <button 
          className={`tab-button ${activeTab === 'whatsapp' ? 'active' : ''}`}
          onClick={() => setActiveTab('whatsapp')}
        >
          WhatsApp
        </button>
        <button 
          className={`tab-button ${activeTab === 'telegram' ? 'active' : ''}`}
          onClick={() => setActiveTab('telegram')}
        >
          Telegram
        </button>
      </div>
      
      <div className="connections-content">
        {activeTab === 'whatsapp' && (
          <div className="connections-tab-content">
            <div className="connections-actions">
              <div className="connections-limit-info">
                <div className="connections-limit-indicator">
                  Conexões WhatsApp: <strong>{connectionsCount}</strong>
                  {' '} / {' '}
                  {planLoading ? 'carregando...' : hasUnlimitedConnections ? 'Ilimitadas' : maxConnections}
                </div>
                {planError && (
                  <div className="connections-limit-warning" role="alert">
                    <AppIcon name="error" />
                    {planError}
                  </div>
                )}
                {!planError && reachedLimit && (
                  <div className="connections-limit-warning" role="alert">
                    <AppIcon name="warning" />
                    Limite de conexões atingido. Faça upgrade do plano para liberar mais conexões.
                  </div>
                )}
              </div>
              <button
                className="new-connection-button"
                onClick={handleNewConnection}
                disabled={reachedLimit}
                title={reachedLimit ? 'Limite de conexões atingido' : 'Adicionar nova conexão'}
              >
                <span className="plus-icon">+</span>
                <span>Nova Conexão</span>
              </button>
            </div>

            <div
              className="connections-meta-cloud-panel"
              style={{
                marginBottom: '1.25rem',
                padding: '1rem 1.25rem',
                borderRadius: 8,
                border: '1px solid #334155',
                background: 'rgba(15, 23, 42, 0.35)',
              }}
            >
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.05rem' }}>WhatsApp Cloud API (Meta)</h3>
              <p style={{ margin: '0 0 0.75rem', fontSize: 13, opacity: 0.9, lineHeight: 1.45 }}>
                O <strong>token de acesso</strong> e o <strong>Phone Number ID</strong> ficam salvos na <strong>empresa</strong> (Configurações). Uma conexão &quot;Cloud API&quot; é criada automaticamente para aparecer em Atendimentos.
              </p>
              <button
                type="button"
                className="new-connection-button"
                onClick={() => navigate('/configuracoes', { state: { openEmpresaTab: true } })}
              >
                Abrir Configurações da empresa
              </button>
            </div>
            
            <div className="connections-table-container">
              <table className="connections-table">
                <thead>
                  <tr>
                    <th>Número</th>
                    <th>Nome da Conexão</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {whatsappConnections.map(connection => (
                    <tr key={connection.id}>
                      <td>{connection.number}</td>
                      <td>{connection.name}</td>
                      <td>
                        <span className="status-indicator">
                          {getStatusIcon(connection.status)}
                          {connection.provider_id === 'meta_cloud' ? (
                            <>
                              {getStatusText(connection.status)}
                              <span style={{ marginLeft: 6, fontSize: 12, opacity: 0.85 }}>(Cloud API)</span>
                            </>
                          ) : connection.status?.toLowerCase() === 'pendente' ? (
                            <>
                              {getStatusText(connection.status)}
                              <button 
                                className="qrcode-button"
                                onClick={() => handleGenerateQRCode(connection.id)}
                                title="Gerar QRCode"
                              >
                                <AppIcon name="qr_code" style={{ marginRight: '4px' }} />
                                Gerar QRCode
                              </button>
                            </>
                          ) : connection.status?.toLowerCase() === 'desconectado' ? (
                            <button 
                              className="qrcode-button"
                              onClick={() => handleGenerateQRCode(connection.id)}
                              title="Gerar QRCode"
                            >
                              <AppIcon name="qr_code" style={{ marginRight: '4px' }} />
                              Gerar QRCode
                            </button>
                          ) : (
                            getStatusText(connection.status)
                          )}

      {/* Preview de imagem em primeiro plano (Portal com z-index máximo, sem classes) */}
      {previewImageUrl && createPortal(
        (
          <div
            onClick={closeImagePreview}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
              zIndex: 2147483647, display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          >
            <button
              onClick={closeImagePreview}
              aria-label="Fechar"
              title="Fechar"
              style={{
                position: 'fixed', top: 16, right: 16,
                width: 40, height: 40, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: '#111827', color: '#e5e7eb', border: '1px solid #374151',
                cursor: 'pointer', boxShadow: '0 4px 16px rgba(0,0,0,0.4)'
              }}
            >
              <AppIcon name="close" style={{ fontSize: 22, lineHeight: 1 }} />
            </button>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '92vw', maxHeight: '92vh', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >
              <img
                src={previewImageUrl}
                alt="Foto de perfil em destaque"
                style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 12 }}
              />
            </div>
          </div>
        ),
        document.body
      )}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="action-btn edit-btn"
                            onClick={() => handleEditConnection(connection.id)}
                            title="Editar conexão"
                          >
                            <AppIcon name="edit" style={{ fontSize: '20px', color: 'white' }} />
                          </button>
                          <button 
                            className="action-btn delete-btn"
                            onClick={() => handleDeleteConnection(connection.id)}
                            title="Excluir conexão"
                          >
                            <AppIcon name="delete" style={{ fontSize: '20px', color: 'white' }} />
                          </button>
                        </div>
                        
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {whatsappConnections.length === 0 && (
                <div className="no-connections">
                  <p>Nenhuma conexão cadastrada. Clique em "Nova Conexão" para adicionar.</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {activeTab === 'telegram' && (
          <div className="connections-tab-content">
            <div className="connections-actions">
              <div className="connections-limit-info">
                <div className="connections-limit-indicator">
                  Conexões Telegram: <strong>{telegramConnections.length}</strong>
                  {' '} / {' '}
                  {planLoading ? 'carregando...' : hasUnlimitedConnections ? 'Ilimitadas' : maxConnections}
                </div>
                {planError && (
                  <div className="connections-limit-warning" role="alert">
                    <AppIcon name="error" />
                    {planError}
                  </div>
                )}
                {!planError && reachedLimit && (
                  <div className="connections-limit-warning" role="alert">
                    <AppIcon name="warning" />
                    Limite de conexões atingido. Faça upgrade do plano para liberar mais conexões.
                  </div>
                )}
              </div>
              <button
                className="new-connection-button"
                onClick={handleNewConnection}
                disabled={reachedLimit}
                title={reachedLimit ? 'Limite de conexões atingido' : 'Adicionar nova conexão'}
              >
                <span className="plus-icon">+</span>
                <span>Nova Conexão</span>
              </button>
            </div>

            <div className="connections-table-container">
              <table className="connections-table">
                <thead>
                  <tr>
                    <th>Número</th>
                    <th>Nome da Conexão</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {telegramConnections.map(connection => (
                    <tr key={connection.id}>
                      <td>{connection.number}</td>
                      <td>{connection.name}</td>
                      <td>
                        <span className="status-indicator">
                          {getStatusIcon(connection.status)}
                          {getStatusText(connection.status)}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="action-btn edit-btn"
                            onClick={() => handleEditConnection(connection.id)}
                            title="Editar conexão"
                          >
                            <AppIcon name="edit" style={{ fontSize: '20px', color: 'white' }} />
                          </button>
                          <button 
                            className="action-btn delete-btn"
                            onClick={() => handleDeleteConnection(connection.id)}
                            title="Excluir conexão"
                          >
                            <AppIcon name="delete" style={{ fontSize: '20px', color: 'white' }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {telegramConnections.length === 0 && (
                <div className="no-connections">
                  <p>Nenhuma conexão Telegram cadastrada. Clique em "Nova Conexão" para adicionar.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Modal para nova conexão */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{activeTab === 'telegram' ? 'Nova Conexão Telegram' : 'Nova Conexão WhatsApp'}</h3>
              <button className="close-button" onClick={handleCloseModal}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="name">Nome da Conexão</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Ex: Atendimento ao Cliente"
                />
              </div>

              {activeTab === 'telegram' && (
                <div className="form-group">
                  <label htmlFor="telegramBotToken">Token do bot (Telegram)</label>
                  <input
                    type="password"
                    id="telegramBotToken"
                    name="telegramBotToken"
                    value={formData.telegramBotToken}
                    onChange={handleInputChange}
                    placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                    autoComplete="off"
                  />
                  <div style={{ marginTop: 12 }}>
                    <label htmlFor="telegramBotUsername" style={{ display: 'block', marginBottom: 6 }}>Username do bot (opcional)</label>
                    <input
                      type="text"
                      id="telegramBotUsername"
                      name="telegramBotUsername"
                      value={formData.telegramBotUsername}
                      onChange={handleInputChange}
                      placeholder="ex: MeuBot"
                      autoComplete="off"
                    />
                  </div>
                  <div style={{ marginTop: 8, fontSize: 13, opacity: 0.9 }}>
                    Obtenha o token criando um bot no{' '}
                    <a href="https://t.me/BotFather" target="_blank" rel="noreferrer">BotFather</a>.
                  </div>
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="description">Descrição</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Descreva o propósito desta conexão"
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="type">Tipo</label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                >
                  <option value="atendimento">Atendimento</option>
                  <option value="automacao">Automação</option>
                  <option value="gestao-interna">Gestão Interna</option>
                </select>
              </div>
              
              <div className="toggle-group">
                {activeTab !== 'telegram' && (
                  <div className="form-group toggle-container">
                    <label className="toggle-label">Sincronizar Contatos</label>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        name="syncContacts"
                        checked={formData.syncContacts}
                        onChange={handleInputChange}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                )}
                
                <div className="form-group toggle-container">
                  <label className="toggle-label">Conectar com IA</label>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      name="connectWithAI"
                      checked={formData.connectWithAI}
                      onChange={handleInputChange}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
              </div>
              
              {formData.connectWithAI && (
                <div className="form-group">
                  <label htmlFor="selectedAgent">Selecione um Agente de IA</label>
                  <select
                    id="selectedAgent"
                    name="selectedAgent"
                    value={formData.selectedAgent}
                    onChange={handleInputChange}
                    disabled={aiAgentsLoading}
                  >
                    {aiAgentsLoading && (
                      <option value="" disabled>Carregando agentes...</option>
                    )}
                    {!aiAgentsLoading && aiAgentsError && (
                      <option value="" disabled>Erro ao carregar agentes</option>
                    )}
                    {!aiAgentsLoading && !aiAgentsError && (
                      <>
                        <option value="">Selecione um agente</option>
                        {aiAgents.length === 0 ? (
                          <option value="" disabled>Nenhum agente encontrado</option>
                        ) : (
                          aiAgents.map(agent => (
                            <option key={agent.id} value={agent.id}>
                              {agent.name}{agent.description ? ` - ${agent.description}` : ''}
                            </option>
                          ))
                        )}
                      </>
                    )}
                  </select>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button className="cancel-button" onClick={handleCloseModal}>
                Cancelar
              </button>
              <button className="save-button" onClick={handleSaveConnection}>
                Salvar Conexão
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição de Conexão */}
      {isEditModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Editar Conexão</h3>
              <button className="close-button" onClick={handleCloseEditModal}>×</button>
            </div>

            <div className="modal-body">
              {activeTab !== 'telegram' && (
                <>
                  {(editProfilePicUrl || editProfileName) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                      {editProfilePicUrl && (
                        <img
                          src={editProfilePicUrl}
                          alt="Foto de perfil"
                          style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', cursor: 'zoom-in' }}
                          onClick={() => openImagePreview(editProfilePicUrl)}
                        />
                      )}
                      {editProfileName && (
                        <div style={{ color: '#e2e8f0', fontSize: '1.15rem', fontWeight: 600 }}>{editProfileName}</div>
                      )}
                    </div>
                  )}

                  {/* Editar nome de perfil do WhatsApp */}
                  <div className="form-group" style={{ marginTop: 8 }}>
                    <label htmlFor="edit-profile-name">Nome de Perfil (WhatsApp)</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        id="edit-profile-name"
                        type="text"
                        value={editProfileNameInput}
                        onChange={(e) => setEditProfileNameInput(e.target.value)}
                        placeholder="Ex: Minha Empresa - Atendimento"
                        style={{ flex: 1 }}
                        disabled={editProfileSaving}
                      />
                      <button
                        type="button"
                        className="save-button"
                        disabled={editProfileSaving || !editingConnectionId || !editProfileNameInput?.trim()}
                        onClick={async () => {
                          if (!editingConnectionId) return;
                          const newName = editProfileNameInput.trim();
                          if (!newName) return;
                          try {
                            setEditProfileSaving(true);
                            const controller = new AbortController();
                            const timer = setTimeout(() => controller.abort(), 20000);
                            const resp = await fetch(API_ENDPOINTS.CONNECTIONS_PROFILE_NAME, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                              body: JSON.stringify({ connection_id: editingConnectionId, name: newName }),
                              signal: controller.signal,
                            }).finally(() => clearTimeout(timer));
                            if (!resp.ok) {
                              const txt = await resp.text().catch(() => '');
                              throw new Error(`Falha ao atualizar nome de perfil (${resp.status})${txt ? ' - ' + txt.substring(0, 200) : ''}`);
                            }
                            // Atualiza visualmente
                            setEditProfileName(newName);
                            // Recarrega detalhes (opcional)
                            try {
                              const detailsResp = await fetch(`${API_ENDPOINTS.CONNECTIONS_DETAILS_BASE}/${editingConnectionId}/details`);
                              if (detailsResp.ok) {
                                const { details } = await detailsResp.json();
                                setEditProfilePicUrl(details?.profile_pic_url || editProfilePicUrl);
                              }
                            } catch {}
                          } catch (e) {
                            const msg = e?.name === 'AbortError' ? 'Tempo esgotado ao atualizar o nome de perfil. Tente novamente.' : (e?.message || 'Falha ao atualizar o nome de perfil');
                            alert(msg);
                          } finally {
                            setEditProfileSaving(false);
                          }
                        }}
                        title="Atualizar nome de perfil"
                      >
                        {editProfileSaving ? 'Atualizando...' : 'Atualizar'}
                      </button>
                    </div>
                  </div>

                  {/* ✅ Botão para alterar imagem do perfil */}
                  <div className="form-group">
                    <button
                      type="button"
                      className="save-button"
                      disabled={editProfileImageSaving || !editingConnectionId}
                      onClick={handleChangeProfileImage}
                      style={{
                        width: '100%',
                        backgroundColor: '#2196F3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                      title="Alterar imagem do perfil do WhatsApp"
                    >
                      <span>🖼️</span>
                      {editProfileImageSaving ? 'Alterando imagem...' : 'Alterar Imagem do Perfil'}
                    </button>
                  </div>
                </>
              )}
              <div className="form-group">
                <label htmlFor="edit-name">Nome da Conexão</label>
                <input
                  type="text"
                  id="edit-name"
                  name="name"
                  value={editFormData.name}
                  onChange={handleEditInputChange}
                  placeholder="Ex: Atendimento ao Cliente"
                />
              </div>

              {activeTab === 'telegram' && (
                <div className="form-group">
                  <label htmlFor="edit-telegramBotToken">Token do bot (Telegram)</label>
                  <input
                    type="password"
                    id="edit-telegramBotToken"
                    name="telegramBotToken"
                    value={editFormData.telegramBotToken}
                    onChange={handleEditInputChange}
                    placeholder="(opcional) Atualizar token"
                    autoComplete="off"
                  />
                  <div style={{ marginTop: 12 }}>
                    <label htmlFor="edit-telegramBotUsername" style={{ display: 'block', marginBottom: 6 }}>Username do bot (opcional)</label>
                    <input
                      type="text"
                      id="edit-telegramBotUsername"
                      name="telegramBotUsername"
                      value={editFormData.telegramBotUsername}
                      onChange={handleEditInputChange}
                      placeholder="ex: MeuBot"
                      autoComplete="off"
                    />
                  </div>
                  <div style={{ marginTop: 8, fontSize: 13, opacity: 0.9 }}>
                    Para gerar um novo token, use o{' '}
                    <a href="https://t.me/BotFather" target="_blank" rel="noreferrer">BotFather</a>.
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="edit-description">Descrição</label>
                <textarea
                  id="edit-description"
                  name="description"
                  value={editFormData.description}
                  onChange={handleEditInputChange}
                  placeholder="Descreva o propósito desta conexão"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-type">Tipo</label>
                <select
                  id="edit-type"
                  name="type"
                  value={editFormData.type}
                  onChange={handleEditInputChange}
                >
                  <option value="atendimento">Atendimento</option>
                  <option value="automacao">Automação</option>
                  <option value="gestao-interna">Gestão Interna</option>
                </select>
              </div>

              <div className="toggle-group">
                {activeTab !== 'telegram' && (
                  <div className="form-group toggle-container">
                    <label className="toggle-label">Sincronizar Contatos</label>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        name="syncContacts"
                        checked={editFormData.syncContacts}
                        onChange={handleEditInputChange}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                )}

                <div className="form-group toggle-container">
                  <label className="toggle-label">Conectar com IA</label>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      name="connectWithAI"
                      checked={editFormData.connectWithAI}
                      onChange={handleEditInputChange}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
              </div>

              {editFormData.connectWithAI && (
                <div className="form-group">
                  <label htmlFor="edit-selectedAgent">Selecione um Agente de IA</label>
                  <select
                    id="edit-selectedAgent"
                    name="selectedAgent"
                    value={editFormData.selectedAgent}
                    onChange={handleEditInputChange}
                    disabled={aiAgentsLoading}
                  >
                    {aiAgentsLoading && (
                      <option value="" disabled>Carregando agentes...</option>
                    )}
                    {!aiAgentsLoading && aiAgentsError && (
                      <option value="" disabled>Erro ao carregar agentes</option>
                    )}
                    {!aiAgentsLoading && !aiAgentsError && (
                      <>
                        <option value="">Selecione um agente</option>
                        {aiAgents.length === 0 ? (
                          <option value="" disabled>Nenhum agente encontrado</option>
                        ) : (
                          aiAgents.map(agent => (
                            <option key={agent.id} value={agent.id}>
                              {agent.name}{agent.description ? ` - ${agent.description}` : ''}
                            </option>
                          ))
                        )}
                      </>
                    )}
                  </select>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="cancel-button" onClick={handleCloseEditModal}>
                Cancelar
              </button>
              <button className="save-button" onClick={handleSaveEdit} disabled={isSavingEdit}>
                {isSavingEdit ? 'Aguarde...' : 'Salvar Alterações'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de QRCode */}
      {qrCodeModal.isOpen && (
        <div className="modal-overlay" onClick={handleCloseQRCodeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Conectar por QrCode</h3>
              <button className="close-button" onClick={handleCloseQRCodeModal}>
                &times;
              </button>
            </div>
            
            <div className="modal-body qrcode-modal">
              {(qrCodeModal.profilePicUrl || qrCodeModal.profileName) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                  {qrCodeModal.profilePicUrl && (
                    <img
                      src={qrCodeModal.profilePicUrl}
                      alt="Foto de perfil"
                      style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', cursor: 'zoom-in' }}
                      onClick={() => openImagePreview(qrCodeModal.profilePicUrl)}
                    />
                  )}
                  {qrCodeModal.profileName && (
                    <div style={{ color: '#e2e8f0', fontSize: '1.15rem', fontWeight: 600 }}>{qrCodeModal.profileName}</div>
                  )}
                </div>
              )}
              <div className="qrcode-container">
                {/* ✅ Mostrar QRCode apenas se não estiver em modo de input de telefone */}
                {!qrCodeModal.showPhoneInput && (
                  <>
                    <div style={{
                      padding: '12px 14px',
                      marginBottom: 16,
                      background: 'rgba(26, 32, 44, 0.55)',
                      border: '1px solid rgba(148, 163, 184, 0.18)',
                      borderRadius: 8
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>⚠️</span>
                        <span>Recomendação Importante: WhatsApp Business</span>
                      </div>
                      <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.4' }}>
                        É ALTAMENTE RECOMENDADO usar contas do WhatsApp Business em vez do WhatsApp normal para integração. O WhatsApp normal pode apresentar inconsistências, desconexões, limitações e instabilidades durante o uso com a nossa API.
                      </p>
                    </div>

                    <img 
                      src={qrCodeModal.qrcodeData} 
                      alt="QR Code para conexão" 
                      className="qrcode-image"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleGenerateQRCode(qrCodeModal.connectionId)}
                      title="Clique para gerar novo QRCode"
                    />
                    {qrLoading && (
                      <p style={{ color: '#a8b3c9', marginTop: '8px', textAlign: 'center' }} aria-live="polite">
                        Carregando QRCode...
                      </p>
                    )}
                    {!!qrError && !qrLoading && (
                      <div style={{ color: '#ff6b6b', marginTop: '8px', textAlign: 'center' }}>
                        {qrError}
                      </div>
                    )}
                    
                    <p className="qrcode-instructions">
                      Para conectar o WhatsApp, siga estes passos:
                    </p>
                    <ol className="qrcode-steps">
                      <li>Abra o WhatsApp no seu celular</li>
                      <li>Clique nas configurações (três pontos) no canto superior direito</li>
                      <li>Selecione <strong>Dispositivos conectados</strong></li>
                      <li>Toque em <strong>Conectar dispositivo</strong></li>
                      <li>Aponte a câmera do celular para este código QR</li>
                    </ol>
                    <p className="qrcode-note">
                      Este código QR expira periodicamente e é atualizado automaticamente a cada 30 segundos.
                    </p>
                  </>
                )}
                
                {/* ✅ Input de telefone para pareamento */}
                {qrCodeModal.showPhoneInput && (
                  <div style={{ marginTop: '16px' }}>
                    <label htmlFor="phone-input" style={{ display: 'block', marginBottom: '8px', color: '#e2e8f0' }}>
                      Número de Telefone (formato internacional, ex: 5511999999999)
                    </label>
                    <input
                      id="phone-input"
                      type="text"
                      value={phoneInput}
                      onChange={(e) => setPhoneInput(e.target.value)}
                      placeholder="5511999999999"
                      style={{
                        width: '100%',
                        padding: '10px',
                        marginBottom: '12px',
                        borderRadius: '4px',
                        border: '1px solid #4a5568',
                        backgroundColor: '#2d3748',
                        color: '#e2e8f0',
                        fontSize: '14px'
                      }}
                    />
                    <button
                      onClick={() => {
                        if (!phoneInput.trim()) {
                          alert('Por favor, informe um número de telefone');
                          return;
                        }
                        handleGenerateQRCode(qrCodeModal.connectionId, { phone: phoneInput });
                        setQrCodeModal(prev => ({ ...prev, showPhoneInput: false }));
                      }}
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: '#4caf50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginBottom: '8px'
                      }}
                    >
                      Gerar com Telefone
                    </button>
                    <button
                      onClick={() => setQrCodeModal(prev => ({ ...prev, showPhoneInput: false }))}
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: '#666',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Voltar
                    </button>
                  </div>
                )}
                
                {/* ✅ Botões de ação (mostrar apenas quando não estiver em modo de input) */}
                {!qrCodeModal.showPhoneInput && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
                    <button
                      onClick={() => handleGenerateQRCode(qrCodeModal.connectionId)}
                      style={{
                        flex: 1,
                        padding: '10px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Gerar por QRCode
                    </button>
                    <button
                      onClick={() => setQrCodeModal(prev => ({ ...prev, showPhoneInput: true }))}
                      style={{
                        flex: 1,
                        padding: '10px',
                        backgroundColor: '#FF9800',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Parear com Número
                    </button>
                  </div>
                )}

              </div>
            </div>
            
            <div className="modal-footer">
              <button className="cancel-button" onClick={handleCloseQRCodeModal}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectionsPage;
